import {
  RootfiDataModelsPrefix,
  RootfiDataModelV4,
} from '@commenda/types/src/root-fi/types';
import {
  RootFiConnectionStatus,
  RootFiIntegration,
  RootfiSyncStatus,
} from '@prisma/client';
import { FALLBACK_SCOPE_DESCRIPTION, V4_SCOPE_DESCRIPTIONS } from './constants';

enum TaxCalculationSupportState {
  Supported,
  ManagedOutsideCommenda,
  NotSupported,
}

const TAX_CALCULATION_SUPPORT_STATE_BY_INTEGRATION: Record<
  RootFiIntegration,
  TaxCalculationSupportState
> = {
  [RootFiIntegration.ZOHO_BOOKS]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.QUICKBOOKS_SANDBOX]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.QUICKBOOKS]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.XERO]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.TALLY]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.SAGE_CLOUD_ACCOUNTING]:
    TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.MS_DYNAMICS_365]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.SAGE_ZA_CLOUD_ACCOUNTING]:
    TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.MYOB_BUSINESS]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.WAVE]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.ODOO_ACCOUNTING]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.NETSUITE]:
    TaxCalculationSupportState.ManagedOutsideCommenda,
  [RootFiIntegration.WAFEQ]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.FRESHBOOKS]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.MEKARI_JURNAL]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.BUSY]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.ROOTFI_SANDBOX]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.QOYOD]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.SHOPIFY]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.STRIPE]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.RAZORPAY]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.PAYPAL]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.HUBSPOT]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.ZOHO_CRM]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.PIPEDRIVE]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.WOO_COMMERCE]:
    TaxCalculationSupportState.ManagedOutsideCommenda,
  [RootFiIntegration.BIG_COMMERCE]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.AMAZON]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.PUZZLE]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.PUZZLE_SANDBOX]: TaxCalculationSupportState.NotSupported,
  [RootFiIntegration.RILLET]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.RILLET_SANDBOX]: TaxCalculationSupportState.Supported,
  [RootFiIntegration.EPICOR_PROPHET_21]:
    TaxCalculationSupportState.NotSupported,
};

const getTaxCalculationSupportState = (
  integration?: RootFiIntegration | null
): TaxCalculationSupportState | null =>
  integration
    ? TAX_CALCULATION_SUPPORT_STATE_BY_INTEGRATION[integration]
    : null;

export const isTaxCalculationSupported = (
  integration?: RootFiIntegration | null
) =>
  getTaxCalculationSupportState(integration) ===
  TaxCalculationSupportState.Supported;

export const isCalculationSettingsManagedOutsideCommenda = (
  integration?: RootFiIntegration | null
) =>
  getTaxCalculationSupportState(integration) ===
  TaxCalculationSupportState.ManagedOutsideCommenda;

export const getRootfiConnectionStatusColor = (
  status: RootFiConnectionStatus
) => {
  if (status === RootFiConnectionStatus.CONNECTED) return 'success';
  if (status === RootFiConnectionStatus.PENDING) return 'warning';
  if (status === RootFiConnectionStatus.DELETED) return 'gray';
  return 'error';
};

export const getRootfiConnectionStatusDotColor = (
  status: RootFiConnectionStatus
) => {
  if (status === RootFiConnectionStatus.CONNECTED)
    return 'bg-utility-success-500';
  if (status === RootFiConnectionStatus.PENDING)
    return 'bg-utility-warning-500';
  if (status === RootFiConnectionStatus.EXPIRED) return 'bg-utility-gray-500';
  return 'bg-utility-error-500';
};

export const getRootfiConnectionStatusText = (
  status: RootFiConnectionStatus
) => {
  if (status === RootFiConnectionStatus.CONNECTED) return 'Connected';
  if (status === RootFiConnectionStatus.PENDING) return 'Connection pending';
  if (status === RootFiConnectionStatus.EXPIRED) return 'Expired';
  return 'Unlinked';
};

export const getRootfiSyncStatusText = (status: RootfiSyncStatus) => {
  switch (status) {
    case RootfiSyncStatus.NOT_STARTED || RootfiSyncStatus.PENDING:
      return 'Sync Pending';
    case RootfiSyncStatus.COMPLETED:
      return 'Sync Complete';
    case RootfiSyncStatus.FAILED:
      return 'Sync Error';
    case RootfiSyncStatus.IN_PROGRESS:
      return 'Syncing';
    default:
      return 'Up to Date';
  }
};

export const getRootfiSyncStatusDotColor = (status: RootfiSyncStatus) => {
  if (status === RootfiSyncStatus.COMPLETED) return 'bg-utility-blue-500';
  if (status === RootfiSyncStatus.FAILED) return 'bg-utility-error-500';
  if (status === RootfiSyncStatus.IN_PROGRESS) return 'bg-utility-gray-500';
  if (status === RootfiSyncStatus.NOT_STARTED || RootfiSyncStatus.PENDING)
    return 'bg-utility-blue-500';
  return 'bg-utility-error-500';
};

export const getRootfiSyncStatusColor = (status: RootfiSyncStatus) => {
  switch (status) {
    case RootfiSyncStatus.COMPLETED:
      return 'blue';
    case RootfiSyncStatus.FAILED:
      return 'error';
    case RootfiSyncStatus.IN_PROGRESS:
      return 'gray';
    case RootfiSyncStatus.NOT_STARTED || RootfiSyncStatus.PENDING:
      return 'blue';
    default:
      return 'gray';
  }
};

export const getScopeDescriptionV4 = (dataModel: RootfiDataModelV4) => {
  return V4_SCOPE_DESCRIPTIONS[dataModel] ?? FALLBACK_SCOPE_DESCRIPTION;
};

export const dateTimeStampToNativeDateTimeStamp = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  // For times less than 24 hours ago, show relative time
  if (diffInDays < 1) {
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  }

  // For dates within the current year, show month and day
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  }

  // For older dates, include the year
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

export const formatDateTime = (date: Date): string => {
  return date
    .toLocaleString('en-US', {
      month: 'short', // "Jan", "Feb", etc.
      day: 'numeric', // 1-31
      year: 'numeric', // 2024
      hour: 'numeric', // 1-12
      minute: '2-digit', // 01-59
      hour12: true, // Use AM/PM
    })
    .replace(',', ''); // Remove comma between date and time
};

export const encodeStripeAccountId = (accountId: string) => {
  return Buffer.from(accountId, 'utf-8').toString('base64');
};

export const formatDataModelName = (dataModel: string): string => {
  // Dynamically remove all category prefixes from RootfiDataModelsPrefix
  let formattedName = dataModel;

  // Remove all category prefixes automatically
  Object.values(RootfiDataModelsPrefix).forEach((prefix) => {
    formattedName = formattedName.replaceAll(prefix, '');
  });

  // Remove underscores and format
  return formattedName
    .replaceAll('_', ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
